import React from 'react'

export const MODEL_KINDS = [
  { value: 'bt_portable', label: 'BT Portable' },
  { value: 'bt_desktop', label: 'BT Desktop' },
  { value: 'tcp_desktop', label: 'TCP Desktop' },
  { value: 'usb_desktop', label: 'USB Desktop' },
]

/** Model options for Tenant printers page (USB Desktop not offered for new/edited rows there). */
export const MODEL_KINDS_TENANT_PRINTERS = MODEL_KINDS.filter((m) => m.value !== 'usb_desktop')

export const BRANDS = [{ value: 'STAR', label: 'STAR' }]

export const GSL_TYPES = [{ value: 'api', label: 'API' }]

export const emptyForm = () => ({
  label: '',
  enable_printer: true,
  printer_default_gateway: '',
  printer_ip_address: '',
  printer_ip_subnet_mask: '',
  printer_model_number: '',
  printer_port_number: '',
  enable_payment: false,
  printer_brand: 'STAR',
  printer_model_kind: 'bt_portable',
  gsl_type: 'api',
  enable_gsl_receipt: true,
  sort_order: 0,
})

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        checked ? 'bg-emerald-500' : 'bg-gray-300'
      }`}
    >
      <span
        className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition"
        style={{ transform: checked ? 'translateX(22px)' : 'translateX(4px)' }}
      />
      <span className="sr-only">{label}</span>
    </button>
  )
}

/**
 * Shared receipt printer fields (tenant_printers) for Tenant printers page and Device detail panel.
 * @param {'full' | 'deviceApproval' | 'tenantPrinters'} [variant] — `deviceApproval` hides payment / GSL (device panel).
 *   `tenantPrinters` hides GSL only and omits USB Desktop from model list; `full` shows all controls.
 */
export function PrinterSettingsFields({ form, onFieldChange, variant = 'full' }) {
  const patch = (key, val) => onFieldChange(key, val)
  const slim = variant === 'deviceApproval'
  const hideGsl = slim || variant === 'tenantPrinters'
  const hidePayment = slim
  const modelKindOptions = variant === 'tenantPrinters' ? MODEL_KINDS_TENANT_PRINTERS : MODEL_KINDS

  return (
    <div className="space-y-4">
      <label className="block">
        <span className="text-xs font-medium text-gray-500">Label</span>
        <input
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={form.label}
          onChange={(e) => patch('label', e.target.value)}
          placeholder="e.g. Floor receipt printer"
        />
      </label>

      <div className="flex items-center justify-between py-1">
        <span className="text-sm text-gray-800">Enable printer</span>
        <Toggle checked={form.enable_printer} onChange={(v) => patch('enable_printer', v)} label="Enable" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Printer default gateway</span>
          <input
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
            value={form.printer_default_gateway}
            onChange={(e) => patch('printer_default_gateway', e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Printer IP / BT address</span>
          <input
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
            value={form.printer_ip_address}
            onChange={(e) => patch('printer_ip_address', e.target.value)}
            placeholder="e.g. BT: PRNT Star"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Subnet mask</span>
          <input
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
            value={form.printer_ip_subnet_mask}
            onChange={(e) => patch('printer_ip_subnet_mask', e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Model number</span>
          <input
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.printer_model_number}
            onChange={(e) => patch('printer_model_number', e.target.value)}
            placeholder="e.g. T300i"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Port</span>
          <input
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.printer_port_number}
            onChange={(e) => patch('printer_port_number', e.target.value)}
            placeholder="e.g. mini"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Sort order</span>
          <input
            type="number"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.sort_order}
            onChange={(e) => patch('sort_order', parseInt(e.target.value, 10) || 0)}
          />
        </label>
      </div>

      {!hidePayment ? (
        <div className="flex items-center justify-between py-1 border-t border-gray-100 pt-3">
          <span className="text-sm text-gray-800">Enable payment</span>
          <Toggle checked={form.enable_payment} onChange={(v) => patch('enable_payment', v)} label="Payment" />
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Printer brand *</span>
          <select
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.printer_brand}
            onChange={(e) => patch('printer_brand', e.target.value)}
          >
            {BRANDS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Printer model *</span>
          <select
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.printer_model_kind}
            onChange={(e) => patch('printer_model_kind', e.target.value)}
          >
            {modelKindOptions.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        {!hideGsl ? (
          <label className="block">
            <span className="text-xs font-medium text-gray-500">GSL type *</span>
            <select
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={form.gsl_type}
              onChange={(e) => patch('gsl_type', e.target.value)}
            >
              {GSL_TYPES.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {!hideGsl ? (
        <div className="flex items-center justify-between py-1">
          <span className="text-sm text-gray-800">Enable GSL receipt</span>
          <Toggle
            checked={form.enable_gsl_receipt}
            onChange={(v) => patch('enable_gsl_receipt', v)}
            label="GSL receipt"
          />
        </div>
      ) : null}
    </div>
  )
}
